import { Clock } from 'lucide-react';
import type { CrmEntry } from '@/entities/crm-entry';
import { UserAvatar } from '@/entities/user';
import { cn } from '@/shared/lib/cn';
import { formatRelative } from '@/shared/lib/format';
import { STAGE_COLORS } from './crm-stage-colors';

export type CrmKanbanCardProps = {
  entry: CrmEntry;
  onClick?: () => void;
  className?: string;
};

const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(value);

const daysBetween = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  return Math.floor(diff / 86_400_000);
};

export const CrmKanbanCard = ({ entry, onClick, className }: CrmKanbanCardProps) => {
  const aging = daysBetween(entry.updatedAt);
  const isCold = aging >= 7;
  const stage = STAGE_COLORS[entry.stage];
  const valor =
    entry.valorPotencial != null ? formatBRL(entry.valorPotencial) : null;
  const ariaLabel = `Lead: ${entry.participanteNome}, estágio ${stage.label}${
    valor ? `, valor ${valor}` : ''
  }`;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={cn(
        // Tint pastel -50 no card inteiro (estilo Notion Kanban): cor do
        // estagio presente mas quase imperceptivel. Border -100 da mesma
        // familia delimita o card sem reforcar cor. Texto continua
        // gray-900/500 (contraste WCAG AA ok em todos os tons -50).
        'group flex w-full flex-col gap-3 rounded-xl border p-3.5 text-left shadow-[0_1px_2px_rgba(16,24,40,0.05)] transition-all duration-150 hover:shadow-sm active:scale-[0.98]',
        stage.bg,
        stage.border,
        className,
      )}
    >
      <div className="flex items-start gap-2.5">
        <UserAvatar
          nome={entry.participanteNome}
          fotoUrl={entry.participanteFotoUrl ?? null}
          size="sm"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900">{entry.participanteNome}</p>
          {valor ? <p className="truncate text-xs text-gray-500">{valor}</p> : null}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-500">
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3 w-3 text-gray-400" />
          {formatRelative(entry.updatedAt)}
        </span>
        {isCold ? (
          <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
            Fria · {aging}d
          </span>
        ) : null}
      </div>
    </button>
  );
};
